import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check, Loader2, AlertCircle, Heart } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session_id');
    
    const [status, setStatus] = useState('loading'); // loading, success, error
    const [paymentData, setPaymentData] = useState(null);
    const [pollCount, setPollCount] = useState(0);

    useEffect(() => {
        if (!sessionId) {
            setStatus('error');
            return;
        }

        const pollPaymentStatus = async () => {
            try {
                const response = await axios.get(`${API}/api/payments/status/${sessionId}`);
                const data = response.data;
                
                if (data.payment_status === 'paid') {
                    setPaymentData(data);
                    setStatus('success');
                    return true;
                } else if (data.status === 'expired') {
                    setStatus('error');
                    return true;
                }
                return false;
            } catch (error) {
                console.error('Error checking payment status:', error);
                if (pollCount >= 4) {
                    setStatus('error');
                    return true;
                }
                return false;
            }
        };

        const startPolling = async () => {
            const completed = await pollPaymentStatus();
            if (!completed && pollCount < 5) {
                setTimeout(() => {
                    setPollCount(prev => prev + 1);
                }, 2000);
            }
        };

        startPolling();
    }, [sessionId, pollCount]);

    if (status === 'loading') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 flex items-center justify-center p-4">
                <Card className="max-w-md w-full bg-slate-900 border-slate-700">
                    <CardContent className="p-8 text-center">
                        <Loader2 className="w-16 h-16 text-indigo-500 animate-spin mx-auto mb-6" />
                        <h2 className="text-2xl font-bold text-white mb-2">Processing Payment...</h2>
                        <p className="text-slate-400">Please wait while we confirm your payment.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 flex items-center justify-center p-4">
                <Card className="max-w-md w-full bg-slate-900 border-slate-700">
                    <CardContent className="p-8 text-center">
                        <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                            <AlertCircle className="w-8 h-8 text-red-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-2">Payment Issue</h2>
                        <p className="text-slate-400 mb-6">
                            We couldn't confirm your payment. Please check your email for confirmation or try again.
                        </p>
                        <div className="flex gap-3 justify-center">
                            <Button
                                variant="outline"
                                onClick={() => navigate('/plans')}
                            >
                                Try Again
                            </Button>
                            <Button
                                onClick={() => navigate('/login')}
                                className="bg-indigo-500 hover:bg-indigo-600"
                            >
                                Go to Login
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 flex items-center justify-center p-4">
            <Card className="max-w-md w-full bg-slate-900 border-slate-700">
                <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                        <Check className="w-10 h-10 text-emerald-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">Payment Successful!</h2>
                    <p className="text-slate-400 mb-6">
                        Welcome to Autoimmune Pathways! Your {paymentData?.metadata?.plan || 'subscription'} plan is now active.
                    </p>
                    
                    <div className="bg-slate-800/50 rounded-lg p-4 mb-6 text-left">
                        <div className="flex justify-between text-sm mb-2">
                            <span className="text-slate-400">Plan</span>
                            <span className="text-white capitalize">{paymentData?.metadata?.plan || 'Premium'}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Billing</span>
                            <span className="text-white capitalize">{paymentData?.metadata?.billing_period || 'Monthly'}</span>
                        </div>
                    </div>
                    
                    <p className="text-sm text-slate-500 mb-6">
                        Now create your account to get started with tracking your health journey.
                    </p>
                    
                    <Button
                        onClick={() => navigate('/signup', { 
                            state: { 
                                plan: paymentData?.metadata?.plan,
                                paymentCompleted: true,
                                sessionId: sessionId
                            } 
                        })}
                        className="w-full bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700"
                    >
                        Create Your Account
                    </Button>
                    
                    <p className="text-xs text-slate-500 mt-4">
                        Already have an account? <Link to="/login" className="text-indigo-400 hover:text-indigo-300">Login here</Link>
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}
